import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../features/navigation/Navigation';
import Footer from '../features/navigation/Footer';
import './LegalPages.styles.css';

// * [PAGE] Statutory Privacy Policy under India's Digital Personal Data Protection Act, 2023 (DPDP Act)
export const PrivacyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Privacy Policy | Yash Haldiya';
  }, []);

  return (
    <div className="legal-page-layout">
      <Navigation />

      <main className="legal-page-container">
        <div className="legal-back-nav">
          <Link to="/" className="legal-back-btn">
            <i className="fas fa-arrow-left" /> Back to Portfolio
          </Link>
        </div>

        <article className="legal-document-card">
          <header className="legal-header">
            <span className="legal-badge">Statutory DPDP Notice</span>
            <h1 className="legal-title">Privacy Policy</h1>
            <p className="legal-meta">
              Governed under the Digital Personal Data Protection Act, 2023 (DPDP Act, India) • Effective Date: October 1, 2026
            </p>
          </header>

          <div className="legal-body">
            <section className="legal-section">
              <h2>1. Introduction & Data Fiduciary Identity</h2>
              <p>
                This Privacy Policy outlines how personal data is collected, processed, stored, and protected when you visit the professional portfolio website of <strong>Yash Haldiya</strong> (&quot;Website&quot;).
              </p>
              <div className="legal-highlight-box">
                <p><strong>Data Fiduciary:</strong> Yash Haldiya</p>
                <p><strong>Registered Jurisdiction & Address:</strong> Jaipur, Rajasthan 302001, India</p>
                <p><strong>Official Contact & Grievance Email:</strong> <a href="mailto:yashaldiya@gmail.com">yashaldiya@gmail.com</a></p>
              </div>
            </section>

            <section className="legal-section">
              <h2>2. Categories of Personal Data Collected</h2>
              <p>We believe in strict data minimization. We collect only what is strictly necessary:</p>
              <ul>
                <li>
                  <strong>Communication Details:</strong> When you submit a message via our Contact form, we collect your <em>Full Name</em>, <em>Email Address</em>, <em>Subject</em>, and <em>Message Content</em>.
                </li>
                <li>
                  <strong>Anonymous Telemetry & Cookies:</strong> With your explicit affirmative consent, we collect aggregated diagnostic data (such as browser type, approximate geographic region, page view sequences, and device metrics) via Google Analytics to optimize site performance.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>3. Purpose of Processing & Lawful Grounds</h2>
              <p>
                In compliance with Section 4 and Section 6 of the DPDP Act 2023, your personal data is processed solely on the basis of your <strong>free, specific, informed, unconditional, and unambiguous consent</strong> for the following purposes:
              </p>
              <ul>
                <li>To evaluate, respond to, and fulfill professional inquiries, employment offers, and engineering collaborations.</li>
                <li>To maintain the security, availability, and technical integrity of this Website.</li>
                <li>To generate aggregated telemetry insights strictly after affirmative consent.</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>4. Third-Party Data Processors</h2>
              <p>We do not sell, rent, or trade your personal data. We utilize trustworthy infrastructure providers who adhere to data protection standards:</p>
              <ul>
                <li><strong>Cloudflare Pages:</strong> Content delivery network and edge hosting.</li>
                <li><strong>Telegram Bot API:</strong> Instant webhook relay used to notify Yash Haldiya of incoming messages without persisting messages in third-party marketing databases.</li>
                <li><strong>Google Analytics (gtag.js):</strong> Aggregated site telemetry, governed strictly by your affirmative consent preference.</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>5. Data Retention & Erasure</h2>
              <p>
                In alignment with the Purpose Limitation principle, communication data is retained only for the duration necessary to satisfy the correspondence or resulting collaboration. Once the purpose has expired or upon your request for erasure, data is permanently deleted.
              </p>
            </section>

            <section className="legal-section">
              <h2>6. Your Statutory Rights as a Data Principal</h2>
              <p>Under Chapter III of the DPDP Act 2023, you possess the following enforceable rights:</p>
              <ul>
                <li><strong>Right to Access:</strong> You may request a summary of personal data being processed and the processing activities undertaken.</li>
                <li><strong>Right to Correction & Erasure:</strong> You have the right to request correction of inaccurate or misleading data, completion of incomplete data, or erasure of your personal data.</li>
                <li><strong>Right of Grievance Redressal:</strong> You have the right to accessible grievance redressal mechanisms provided by the Data Fiduciary.</li>
                <li><strong>Right to Nominate:</strong> You have the right to nominate another individual to exercise rights on your behalf in the event of death or incapacity.</li>
                <li><strong>Right to Withdraw Consent:</strong> You may withdraw your consent at any time as easily as it was granted by contacting the Grievance Officer or clearing your cookie settings.</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>7. Grievance Redressal Mechanism & Appellate Process</h2>
              <p>
                If you have questions, wish to exercise any of your statutory Data Principal rights, or have a grievance concerning data processing, please contact the designated Grievance Officer:
              </p>
              <div className="legal-highlight-box">
                <p><strong>Grievance Redressal Officer:</strong> Yash Haldiya</p>
                <p><strong>Email:</strong> <a href="mailto:yashaldiya@gmail.com">yashaldiya@gmail.com</a></p>
                <p><strong>Response Timeline:</strong> Within 30 calendar days from receipt of inquiry.</p>
              </div>
              <p>
                If you are unsatisfied with the resolution provided by the Data Fiduciary, you have the statutory right under the DPDP Act 2023 to file a complaint with the <strong>Data Protection Board of India (DPBI)</strong>.
              </p>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
